import {
  Button,
  Center,
  Loader,
  TextInput,
  Textarea,
  Flex,
  MultiSelect,
} from '@mantine/core';
import { useBusiness, useUser } from '../../../hooks';
import { useTranslation } from 'react-i18next';
import { GenericErrorFallback } from '../../../shared/components/GenericErrorFallback';
import DevToolsCard from './DevToolsCard';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import classes from './DevTools.module.css';
import {
  useCreateWebhook,
  useListWebhooks,
  useWebhookSupportedEvents,
} from '../../../hooks/webhooksApi';

export type WebhookAddFormData = {
  url: string;
  description: string;
  events: string[];
};

const WEBHOOK_LIST_ROUTE = '/admin/dev-tools/webhooks';

export const WebhooksAdd = () => {
  const { t } = useTranslation();
  const { data: user, isError } = useUser();
  const navigate = useNavigate();
  const { data: business, isLoading } = useBusiness(user?.businessId);

  const createWebhook = useCreateWebhook();
  const listWebhooks = useListWebhooks();
  const supportedEvents = useWebhookSupportedEvents();

  const formSchema = yup.object().shape({
    url: yup
      .string()
      .url()
      // force to require https://
      .matches(/^https:\/\//, t<string>('validations.urlHttps'))
      .required()
      .max(2048),
    description: yup.string().optional().max(1000),
    events: yup
      .array()
      .of(yup.string())
      .required(t<string>('devTools.webhooksAdd.eventsRequired')),
  });

  const form = useForm<WebhookAddFormData>({
    resolver: yupResolver(formSchema),
    mode: 'onBlur',
  });

  const handleSubmit = async (data: WebhookAddFormData) => {
    await createWebhook.mutateAsync({
      url: data.url,
      description: data.description,
      events: data.events,
    });
    await listWebhooks.refetch();

    navigate(WEBHOOK_LIST_ROUTE);
  };

  if (isLoading) {
    return (
      <Center w="100%" h="100vh">
        <Loader />
      </Center>
    );
  }

  if (isError || !business || !user) {
    return <GenericErrorFallback />;
  }

  const {
    formState: { errors, isSubmitting },
    register,
    control,
  } = form;

  if (supportedEvents.isLoading) {
    return <Loader />;
  }

  return (
    <DevToolsCard title={t('devTools.webhooksAdd.title')}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <TextInput
            mt="md"
            {...register('url')}
            label={t('devTools.webhooks.url')}
            classNames={{
              input: classes.darkBackground,
            }}
            error={errors.url?.message}
            placeholder="https://example.com/webhook"
          />

          <Controller
            name="events"
            control={control}
            render={({ field }) => (
              <MultiSelect
                {...field}
                mt="md"
                multiple
                label={t('devTools.webhooks.events')}
                data={supportedEvents?.data?.events?.map((event) => ({
                  label: event,
                  value: event,
                }))}
                searchable
                style={{ flex: '0 0 228px' }}
                disabled={supportedEvents.isLoading || supportedEvents.isError}
                placeholder={t<string>(
                  'devTools.webhooksAdd.eventsPlaceholder'
                )}
                classNames={{
                  input: classes.darkBackground,
                  pill: classes.darkPill,
                }}
                error={errors.events?.message}
              />
            )}
          />

          <Textarea
            mt="md"
            {...register('description')}
            label={t('devTools.webhooks.description')}
            placeholder={t<string>(
              'devTools.webhooksAdd.descriptionPlaceholder'
            )}
            classNames={{
              input: classes.darkBackground,
            }}
            error={errors.description?.message}
            maxLength={1000}
          />

          <Flex justify="end" gap="sm" mt="lg">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(WEBHOOK_LIST_ROUTE)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              type="submit"
              loading={isSubmitting}
            >
              {t('common.create')}
            </Button>
          </Flex>
        </form>
      </FormProvider>
    </DevToolsCard>
  );
};
